import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Avatar, Menu, MenuItem, Divider,
  Chip, Tooltip, ListItemIcon, ListItemText, Stack,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import PlaceIcon from '@mui/icons-material/Place';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';

const TopBar = ({ onMenuClick, isMobile }) => {
  const { user, logout } = useAuth();
  const {
    empresa, sucursales, sucursalActiva, sucursalActivaId,
    hayVariasSucursales,
    seleccionarSucursal, cambiarEmpresa,
  } = useApp();
  const navigate = useNavigate();
  const [anchorUser, setAnchorUser]     = useState(null);
  const [anchorSucursal, setAnchorSuc]  = useState(null);

  const handleLogout = () => {
    setAnchorUser(null);
    logout();
    navigate('/login');
  };

  const now = new Date().toLocaleDateString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const esSuperAdmin  = user?.entidadTipo === 'SUPERADMIN';
  const nombreEmpresa = empresa?.nombreComercial || empresa?.NombreComercial
                     || empresa?.razonSocial     || empresa?.RazonSocial;

  // Etiqueta del selector de sucursal
  let labelSelector = 'Cargando…';
  let iconSelector = <StorefrontIcon />;
  if (empresa && sucursales) {
    if (sucursales.length === 0) {
      labelSelector = 'Sin sucursales';
      iconSelector = <PlaceIcon />;
    } else if (sucursales.length === 1) {
      const s = sucursales[0];
      labelSelector = s.nombreCorto || s.NombreCorto || s.nombre || s.Nombre;
      iconSelector = <StorefrontIcon />;
    } else if (sucursalActiva) {
      labelSelector = sucursalActiva.nombreCorto || sucursalActiva.NombreCorto
                   || sucursalActiva.nombre || sucursalActiva.Nombre;
      iconSelector = <StorefrontIcon />;
    } else {
      labelSelector = 'Todas las sucursales';
      iconSelector = <AllInboxIcon />;
    }
  }

  const puedeElegirSucursal = hayVariasSucursales;

  return (
    <AppBar position="sticky" color="inherit" elevation={0}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Toolbar sx={{ minHeight: { xs: 60, md: 68 }, gap: 1 }}>
        {isMobile && (
          <IconButton edge="start" onClick={onMenuClick} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary"
                      sx={{ textTransform: 'capitalize', lineHeight: 1 }}>
            {now}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2} noWrap>
            Hola, {user?.nombre?.split(' ')[0]}
          </Typography>
        </Box>

        {/* Empresa activa — solo SUPERADMIN puede cambiarla */}
        {nombreEmpresa && (
          <Tooltip title={esSuperAdmin ? 'Cambiar de empresa' : nombreEmpresa}>
            <Chip
              icon={<StorefrontIcon />}
              label={nombreEmpresa}
              color="default"
              variant="outlined"
              size="small"
              onClick={esSuperAdmin ? cambiarEmpresa : undefined}
              onDelete={esSuperAdmin ? cambiarEmpresa : undefined}
              deleteIcon={esSuperAdmin ? <SwapHorizIcon /> : undefined}
              sx={{
                cursor: esSuperAdmin ? 'pointer' : 'default',
                display: { xs: 'none', md: 'flex' },
                maxWidth: 220,
              }}
            />
          </Tooltip>
        )}

        {/* Selector de sucursal (solo si la empresa tiene varias) */}
        {puedeElegirSucursal && (
          <Chip
            icon={iconSelector}
            label={labelSelector}
            color="primary"
            variant={sucursalActivaId === null ? 'filled' : 'outlined'}
            onClick={(e) => setAnchorSuc(e.currentTarget)}
            deleteIcon={<SwapHorizIcon />}
            onDelete={(e) => setAnchorSuc(e.currentTarget)}
            sx={{ cursor: 'pointer', maxWidth: 260 }}
          />
        )}

        <Menu
          anchorEl={anchorSucursal}
          open={Boolean(anchorSucursal)}
          onClose={() => setAnchorSuc(null)}
          PaperProps={{ sx: { minWidth: 280, maxWidth: 360 } }}
        >
          <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              OPERAR
            </Typography>
            <Typography variant="body2" fontWeight={600}>{nombreEmpresa}</Typography>
          </Box>
          <Divider />

          {/* Opción "Todas las sucursales" */}
          <MenuItem
            selected={sucursalActivaId === null}
            onClick={() => { setAnchorSuc(null); seleccionarSucursal(null); }}
          >
            <ListItemIcon>
              {sucursalActivaId === null
                ? <CheckIcon fontSize="small" color="primary" />
                : <AllInboxIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary="Todas las sucursales"
              secondary="Vista consolidada (dashboard, inventario global, admin)"
            />
          </MenuItem>
          <Divider />

          {sucursales.map((s) => {
            const id = s.sucursalId || s.SucursalId;
            const nombre = s.nombre || s.Nombre;
            const enHV  = s.enHolaVeci    ?? s.EnHolaVeci    ?? false;
            const enPOS = s.enPosCompacto ?? s.EnPosCompacto ?? false;
            return (
              <MenuItem
                key={id}
                selected={sucursalActivaId === id}
                onClick={() => { setAnchorSuc(null); seleccionarSucursal(id); }}
              >
                <ListItemIcon>
                  {sucursalActivaId === id
                    ? <CheckIcon fontSize="small" color="primary" />
                    : <StorefrontIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={nombre}
                  secondary={
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {enHV  && <Chip size="small" label="HolaVeci" color="success"    variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      {enPOS && <Chip size="small" label="Local"    color="primary"    variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      {!enHV && !enPOS && (
                        <Chip size="small" label="Inactiva" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Stack>
                  }
                />
              </MenuItem>
            );
          })}
        </Menu>

        <IconButton onClick={(e) => setAnchorUser(e.currentTarget)}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
            {user?.nombre?.charAt(0)}
          </Avatar>
        </IconButton>

        <Menu anchorEl={anchorUser} open={Boolean(anchorUser)} onClose={() => setAnchorUser(null)}>
          <Box sx={{ px: 2, py: 1, minWidth: 240 }}>
            <Typography variant="subtitle2" fontWeight={600}>{user?.nombre}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.correo || user?.usuario}
            </Typography>
            <Chip label={user?.rol?.nombre} size="small" color="primary"
                  sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
          </Box>
          <Divider />
          {esSuperAdmin && nombreEmpresa && [
            <MenuItem key="cambiar" onClick={() => { setAnchorUser(null); cambiarEmpresa(); }}>
              <SwapHorizIcon fontSize="small" sx={{ mr: 1.5 }} /> Cambiar empresa
            </MenuItem>,
            <Divider key="d-cambiar" />,
          ]}
          <MenuItem onClick={() => { setAnchorUser(null); navigate('/configuracion'); }}>
            <PersonIcon fontSize="small" sx={{ mr: 1.5 }} /> Configuración
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> Cerrar sesión
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
